/**
 * Pure planner for the finding-series background preload (fix C).
 *
 * After the active hanging-protocol panes finish loading, the viewer warms the
 * cornerstone cache for series that carry an AI finding so a finding-click lands
 * instantly instead of cold-loading the whole series (~9.5s for a 600-frame CT).
 *
 * Kept pure (no cornerstone, no atoms) so the selection logic is unit-tested in
 * isolation. The wiring layer supplies the inputs and performs the actual
 * low-priority prefetch for the returned targets.
 *
 * Budget discipline: decoded volumes are only resident for the
 * `VOLUME_LRU_CAP` most-recently-bound series (HangingProtocolGrid LRU) plus an
 * overall byte cap, so preloading more than fits would evict the panes the user
 * is looking at. `maxSeries` is the caller's budget; the plan never exceeds it.
 */

export interface PreloadTarget {
  seriesId: string;
  /** The finding's slice — the preloader fetches this first so the jumped-to
   *  slice is the first thing decoded (ties into the target-slice priority). */
  sliceIndex: number;
}

interface FindingLike {
  seriesId: string | null;
  sliceIndex: number | null;
}

export function planFindingSeriesPreload(params: {
  findings: ReadonlyArray<FindingLike>;
  visibleSeriesIds: ReadonlyArray<string>;
  maxSeries: number;
}): PreloadTarget[] {
  const { findings, visibleSeriesIds, maxSeries } = params;
  if (maxSeries <= 0) return [];

  const visible = new Set(visibleSeriesIds);
  const seen = new Set<string>();
  const plan: PreloadTarget[] = [];

  for (const f of findings) {
    if (plan.length >= maxSeries) break;
    if (f.seriesId == null || f.sliceIndex == null) continue;
    if (visible.has(f.seriesId) || seen.has(f.seriesId)) continue;
    seen.add(f.seriesId);
    plan.push({ seriesId: f.seriesId, sliceIndex: f.sliceIndex });
  }

  return plan;
}
